/**
 * Growtype Analytics — A/B Traffic Splitter
 *
 * Industry-standard synchronous assignment: configs are embedded inline by PHP,
 * cookies read/written synchronously, redirect happens before first paint.
 * No XHR, no flicker, no Flash of Original Content (FOOC).
 *
 * Sticky assignment via 30-day cookie. Variant attribution injected into ALL
 * tracked events via gaTrack() wrapper, enabling per-variant funnel analytics.
 *
 * Patterns match the existing tracker: cookie format, path guards, gaDebug flag.
 */
(function () {
    'use strict';

    var COOKIE_PREFIX = 'ga_ab_';
    var COOKIE_DAYS = 30;
    var DEBUG = window.gaDebug || false;
    var IS_HTTPS = window.location.protocol === 'https:';
    var URL_WATCH_INTERVAL_MS = 1000;

    // Bot/crawler patterns — skip to avoid SEO impact and skewed analytics
    var BOT_PATTERNS = /bot|crawl|spider|slurp|archiver|scan|preview|prerender|lighthouse|pingdom|pagespeed|headless|chrome-lighthouse/i;

    function log() {
        if (DEBUG) {
            var args = Array.prototype.slice.call(arguments);
            args.unshift('[GA A/B]');
            console.log.apply(console, args);
        }
    }

    function info() {
        if (DEBUG) {
            var args = Array.prototype.slice.call(arguments);
            args.unshift('[GA A/B]');
            console.log.apply(console, args);
        }
    }

    function errorLog() {
        if (DEBUG) {
            var args = Array.prototype.slice.call(arguments);
            args.unshift('[GA A/B]');
            console.error.apply(console, args);
        }
    }

    // --- Helpers (matching patterns in growtype-analytics-tracker.js) ---

    function isBot() {
        return BOT_PATTERNS.test(navigator.userAgent || '');
    }

    function getCookie(name) {
        if (!document.cookie) return null;
        var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return m ? decodeURIComponent(m[1]) : null;
    }

    function setCookie(name, value, days) {
        var d = new Date();
        d.setTime(d.getTime() + days * 86400000);
        var cookie = name + '=' + encodeURIComponent(value) +
            '; path=/; expires=' + d.toUTCString() +
            '; SameSite=Lax';
        if (IS_HTTPS) cookie += '; Secure';
        document.cookie = cookie;
        if (typeof window.__gaAbInvalidateVariantCache === 'function') {
            window.__gaAbInvalidateVariantCache();
        }
    }

    // --- Config: reads inline embedded data (set by PHP), no XHR needed ---

    function getConfigs() {
        return window.gaAbConfigs || [];
    }

    function hasConfigs() {
        var configs = getConfigs();
        return Array.isArray(configs) && configs.length > 0;
    }

    function getPathFromUrlLike(urlLike) {
        return (urlLike.pathname || '') + (urlLike.search || '');
    }

    // --- URL matching (mirrors backend pattern logic) ---

    function pathMatches(value, path) {
        if (!value) return false;

        if (value.charAt(0) === '=') return path === value.substring(1);      // exact
        if (value.charAt(0) === '^') return path.indexOf(value.substring(1)) === 0; // starts with
        if (value.indexOf('*') !== -1) {
            var escaped = value.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
            var regex = new RegExp('^' + escaped.replace(/\*/g, '.*') + '$');
            return regex.test(path);
        }
        if (value === '/') return path === '/' || path === '';                  // homepage
        return path.indexOf(value) !== -1;                                      // contains
    }

    function urlMatches(value) {
        return pathMatches(value, getPathFromUrlLike(window.location));
    }

    function isAlreadyOnB(valueB) {
        if (!valueB) return false;
        return urlMatches(valueB);
    }

    // --- Redirect URL builder ---

    function buildRedirectUrlForLocation(valueB, locationLike) {
        if (!valueB) return null;

        // Query-param style: append params if not already present
        if (valueB.charAt(0) === '?' || valueB.charAt(0) === '&') {
            var clean = valueB.replace(/^[?&]+/, '');
            if (clean) {
                var allPresent = true;
                var params = clean.split('&');
                for (var p = 0; p < params.length; p++) {
                    if (params[p] && (locationLike.search || '').indexOf(params[p]) === -1) {
                        allPresent = false;
                        break;
                    }
                }
                if (allPresent) return null;
            }
            var sep = locationLike.search ? '&' : '?';
            return (locationLike.pathname || '') + (locationLike.search || '') + sep + clean + (locationLike.hash || '');
        }

        // Full URL redirect
        if (valueB.indexOf('://') !== -1) return valueB;

        // Absolute path redirect
        if (valueB.charAt(0) === '/') {
            if ((locationLike.pathname || '') === valueB) return null;
            return valueB + (locationLike.search || '') + (locationLike.hash || '');
        }

        return valueB;
    }

    function buildRedirectUrl(valueB) {
        return buildRedirectUrlForLocation(valueB, window.location);
    }

    // --- Tracking (reuses existing gaTrack API) ---

    function trackAssignment(funnelId, stepIndex, variant, label) {
        // Use the existing analytics tracker API: window.gaTrack()
        if (window.gaTrack) {
            window.gaTrack('ab_test_assigned', {
                object_id: 'funnel_' + funnelId + '_step_' + stepIndex,
                object_type: 'ab_test',
                metadata: {
                    funnel_id: funnelId,
                    step_index: stepIndex,
                    variant: variant,
                    label: label || ''
                }
            });
        }
        // Also push to GTM dataLayer if present
        if (window.dataLayer) {
            window.dataLayer.push({
                event: 'ab_test_assigned',
                ga_funnel_id: funnelId,
                ga_step_index: stepIndex,
                ga_variant: variant,
                ga_label: label || ''
            });
        }
    }

    // --- Main A/B processing ---

    function getAssignmentDecision(config, locationLike) {
        var funnelId = config.funnel_id;
        var stepIndex = config.step_index;
        var valueA = config.value;
        var valueB = config.value_b;
        var trafficB = Math.max(0, Math.min(100, parseInt(config.traffic_b, 10) || 50));
        var label = config.label;
        var cookieName = COOKIE_PREFIX + funnelId + '_' + stepIndex;
        var versionCookieName = COOKIE_PREFIX + 'v_' + funnelId + '_' + stepIndex;
        var configVersion = config.version || '';
        var currentUrl = (locationLike.pathname || '') + (locationLike.search || '') + (locationLike.hash || '');

        if (!pathMatches(valueA, getPathFromUrlLike(locationLike))) {
            log('No A-match for config', {
                funnelId: funnelId,
                stepIndex: stepIndex,
                valueA: valueA,
                currentPath: getPathFromUrlLike(locationLike)
            });
            return null;
        }

        var existing = getCookie(cookieName);
        var existingVersion = getCookie(versionCookieName);

        if (configVersion && existing && existingVersion !== configVersion) {
            info('Stale A/B assignment detected, resetting cookie usage', {
                funnelId: funnelId,
                stepIndex: stepIndex,
                existing: existing,
                existingVersion: existingVersion,
                configVersion: configVersion,
                currentUrl: currentUrl
            });
            existing = null;
        }

        if (existing === 'B') {
            return {
                funnelId: funnelId,
                stepIndex: stepIndex,
                label: label,
                variant: 'B',
                redirectUrl: buildRedirectUrlForLocation(valueB, locationLike),
                currentUrl: currentUrl,
                valueB: valueB,
                configVersion: configVersion,
                cookieName: cookieName,
                versionCookieName: versionCookieName
            };
        }

        if (existing === 'A') {
            return {
                funnelId: funnelId,
                stepIndex: stepIndex,
                label: label,
                variant: 'A',
                redirectUrl: null,
                currentUrl: currentUrl,
                valueB: valueB,
                configVersion: configVersion,
                cookieName: cookieName,
                versionCookieName: versionCookieName
            };
        }

        if (pathMatches(valueB, getPathFromUrlLike(locationLike))) {
            return {
                funnelId: funnelId,
                stepIndex: stepIndex,
                label: label,
                variant: 'B',
                redirectUrl: null,
                currentUrl: currentUrl,
                valueB: valueB,
                configVersion: configVersion,
                cookieName: cookieName,
                versionCookieName: versionCookieName,
                shouldPersist: true
            };
        }

        var roll = Math.random() * 100;
        var assigned = roll < trafficB ? 'B' : 'A';

        info('Computed A/B decision', {
            funnelId: funnelId,
            stepIndex: stepIndex,
            label: label,
            currentUrl: currentUrl,
            existing: existing,
            trafficB: trafficB,
            roll: roll,
            assigned: assigned,
            redirectUrl: assigned === 'B' ? buildRedirectUrlForLocation(valueB, locationLike) : null
        });

        return {
            funnelId: funnelId,
            stepIndex: stepIndex,
            label: label,
            variant: assigned,
            redirectUrl: assigned === 'B' ? buildRedirectUrlForLocation(valueB, locationLike) : null,
            currentUrl: currentUrl,
            valueB: valueB,
            configVersion: configVersion,
            cookieName: cookieName,
            versionCookieName: versionCookieName,
            shouldPersist: true
        };
    }

    function persistAssignment(decision) {
        if (!decision) return;
        info('Persisting assignment', {
            funnelId: decision.funnelId,
            stepIndex: decision.stepIndex,
            variant: decision.variant,
            cookieName: decision.cookieName,
            versionCookieName: decision.versionCookieName,
            configVersion: decision.configVersion || null
        });
        setCookie(decision.cookieName, decision.variant, COOKIE_DAYS);
        if (decision.configVersion) {
            setCookie(decision.versionCookieName, decision.configVersion, COOKIE_DAYS);
        }
    }

    function processAbTest(config) {
        var funnelId = config.funnel_id;
        var stepIndex = config.step_index;
        var label = config.label;
        var redirectCountName = COOKIE_PREFIX + 'rc_' + funnelId + '_' + stepIndex + '_' + encodeURIComponent(window.location.pathname + window.location.search);
        var currentUrl = window.location.pathname + window.location.search + window.location.hash;

        // Circuit breaker: max 3 redirects per test per 5 minutes.
        // Uses sessionStorage (persists across redirects in same tab) with cookie fallback.
        var redirectCount = getRedirectCount(redirectCountName);
        if (redirectCount >= 3) {
            errorLog('Redirect limit reached for funnel ' + funnelId + ' — breaking chain', {
                funnelId: funnelId,
                stepIndex: stepIndex,
                redirectCount: redirectCount,
                currentUrl: currentUrl
            });
            return false;
        }

        var decision = getAssignmentDecision(config, window.location);
        if (!decision) {
            log('No decision returned for config', config);
            return false;
        }

        info('Processing A/B decision on page', {
            funnelId: funnelId,
            stepIndex: stepIndex,
            currentUrl: currentUrl,
            variant: decision.variant,
            redirectUrl: decision.redirectUrl || null,
            shouldPersist: !!decision.shouldPersist
        });

        if (decision.variant === 'B') {
            if (decision.shouldPersist) {
                persistAssignment(decision);
            }
            var bUrl = decision.redirectUrl;
            // Triple guard: URL exists, not already on B, not same-as-current
            if (bUrl && bUrl !== currentUrl && !isAlreadyOnB(decision.valueB)) {
                info('Redirecting current page to B variant', {
                    funnelId: funnelId,
                    stepIndex: stepIndex,
                    from: currentUrl,
                    to: bUrl
                });
                trackAssignment(funnelId, stepIndex, 'B', label);
                incrementRedirectCount(redirectCountName);
                window.location.replace(bUrl);
                return true;
            }
            info('Already on B variant or no redirect needed', {
                funnelId: funnelId,
                stepIndex: stepIndex,
                currentUrl: currentUrl,
                redirectUrl: bUrl || null
            });
            trackAssignment(funnelId, stepIndex, 'B', label);
            return false;
        }

        if (decision.variant === 'A' && !decision.shouldPersist) {
            trackAssignment(funnelId, stepIndex, 'A', label);
            return false;
        }

        persistAssignment(decision);

        if (decision.variant === 'B') {
            var url = decision.redirectUrl;
            // Guard: URL exists and is different from current (prevents same-page redirect)
            if (url && url !== currentUrl) {
                trackAssignment(funnelId, stepIndex, 'B', label);
                incrementRedirectCount(redirectCountName);
                window.location.replace(url);
                return true;
            }
        }

        trackAssignment(funnelId, stepIndex, decision.variant, label);
        return false;
    }

    // --- Redirect counter: sessionStorage (tab-scoped) + cookie fallback ---

    function getRedirectCount(name) {
        try {
            var raw = sessionStorage.getItem(name);
            if (raw !== null) return parseInt(raw, 10) || 0;
        } catch (e) {}
        return parseInt(getCookie(name), 10) || 0;
    }

    function incrementRedirectCount(name) {
        var count = getRedirectCount(name) + 1;
        try { sessionStorage.setItem(name, String(count)); } catch (e) {}
        setCookie(name, String(count), 5 / 1440); // 5 min TTL cookie fallback
    }

    // --- Init ---

    function getCurrentUrlKey() {
        return window.location.pathname + window.location.search + window.location.hash;
    }

    function shouldSkipCurrentPage() {
        return window.location.pathname.indexOf('/wp-admin') === 0 ||
            window.location.pathname.indexOf('/wp-json') === 0 ||
            window.location.pathname.indexOf('/wp-content') === 0 ||
            window.location.pathname.indexOf('admin-ajax.php') !== -1;
    }

    function init() {
        if (shouldSkipCurrentPage()) return;

        // Skip bots/crawlers
        if (isBot()) return;

        if (!hasConfigs()) return;

        // Prevent duplicate processing for the exact same URL, but allow
        // SPA/session-switch navigation to be re-evaluated on URL changes.
        var currentUrlKey = getCurrentUrlKey();
        if (window.__gaAbProcessedUrl === currentUrlKey) return;
        window.__gaAbProcessedUrl = currentUrlKey;
        info('Running A/B init', {
            currentUrl: currentUrlKey,
            configsCount: getConfigs().length
        });

        // Configs are embedded inline by PHP — read synchronously, no XHR
        var configs = getConfigs();

        for (var i = 0; i < configs.length; i++) {
            try {
                if (processAbTest(configs[i])) return; // redirect
            } catch (e) {
                errorLog('Error processing test ' + (configs[i].funnel_id || i) + ':', e, configs[i]);
            }
        }
    }

    function scheduleInit(delay) {
        if (window.__gaAbScheduledInitTimer) {
            clearTimeout(window.__gaAbScheduledInitTimer);
        }

        window.__gaAbScheduledInitTimer = setTimeout(function () {
            window.__gaAbScheduledInitTimer = null;
            try {
                log('Scheduled init fired', { currentUrl: getCurrentUrlKey(), delay: delay || 0 });
                init();
            } catch (e) {
                errorLog('Scheduled init failed:', e);
            }
        }, delay || 0);
    }

    function installNavigationListeners() {
        if (!hasConfigs()) return;
        if (window.__gaAbNavigationHooksInstalled) return;
        window.__gaAbNavigationHooksInstalled = true;
        window.__gaAbLastObservedUrl = getCurrentUrlKey();

        function scheduleNavigationRecheck() {
            log('Scheduling navigation recheck', {
                currentUrl: getCurrentUrlKey()
            });
            scheduleInit(0);
        }

        if (window.history && typeof window.history.pushState === 'function') {
            var originalPushState = window.history.pushState;
            window.history.pushState = function () {
                var result = originalPushState.apply(this, arguments);
                scheduleNavigationRecheck();
                return result;
            };
        }

        if (window.history && typeof window.history.replaceState === 'function') {
            var originalReplaceState = window.history.replaceState;
            window.history.replaceState = function () {
                var result = originalReplaceState.apply(this, arguments);
                scheduleNavigationRecheck();
                return result;
            };
        }

        if (window.addEventListener) {
            window.addEventListener('popstate', scheduleNavigationRecheck);
            window.addEventListener('hashchange', scheduleNavigationRecheck);
            window.addEventListener('pageshow', scheduleNavigationRecheck);
        }

        if (document && document.addEventListener) {
            document.addEventListener('click', function (event) {
                if (
                    event.defaultPrevented ||
                    event.button !== 0 ||
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.altKey
                ) {
                    return;
                }

                var target = event.target;
                if (!target || !target.closest) return;

                var sessionLink = target.closest('.g-chatsessions-single[href]');
                if (sessionLink) {
                    var href = sessionLink.getAttribute('href');
                    info('Session link clicked', {
                        currentUrl: getCurrentUrlKey(),
                        href: href
                    });
                    if (href) {
                        try {
                            var absoluteUrl = new URL(href, window.location.href);
                            var locationLike = {
                                pathname: absoluteUrl.pathname,
                                search: absoluteUrl.search,
                                hash: absoluteUrl.hash
                            };
                            var configs = getConfigs();
                            for (var i = 0; i < configs.length; i++) {
                                var decision = getAssignmentDecision(configs[i], locationLike);
                                if (!decision) {
                                    log('Session link produced no decision for config', configs[i]);
                                    continue;
                                }

                                info('Session link decision', {
                                    href: href,
                                    funnelId: decision.funnelId,
                                    stepIndex: decision.stepIndex,
                                    variant: decision.variant,
                                    redirectUrl: decision.redirectUrl || null,
                                    shouldPersist: !!decision.shouldPersist
                                });

                                if (decision.shouldPersist) {
                                    persistAssignment(decision);
                                }

                                if (decision.variant === 'B' && decision.redirectUrl && decision.redirectUrl !== href && decision.redirectUrl !== absoluteUrl.href) {
                                    info('Intercepting session link to force B variant', {
                                        originalHref: href,
                                        redirectedHref: decision.redirectUrl
                                    });
                                    event.preventDefault();
                                    event.stopPropagation();
                                    if (typeof event.stopImmediatePropagation === 'function') {
                                        event.stopImmediatePropagation();
                                    }
                                    window.location.assign(decision.redirectUrl);
                                    return;
                                }
                            }
                        } catch (e) {
                            errorLog('Session link redirect check failed:', e, { href: href });
                        }
                    }
                    scheduleInit(150);
                }
            }, true);
        }

        if (window.MutationObserver && document && document.body) {
            var observer = new MutationObserver(function () {
                var nextUrlKey = getCurrentUrlKey();
                if (nextUrlKey !== window.__gaAbLastObservedUrl) {
                    window.__gaAbLastObservedUrl = nextUrlKey;
                    scheduleNavigationRecheck();
                }
            });
            observer.observe(document.body, { childList: true, subtree: true });
        }

        if (window.setInterval) {
            window.setInterval(function () {
                var nextUrlKey = getCurrentUrlKey();
                if (nextUrlKey !== window.__gaAbLastObservedUrl) {
                    window.__gaAbLastObservedUrl = nextUrlKey;
                    scheduleNavigationRecheck();
                }
            }, URL_WATCH_INTERVAL_MS);
        }
    }

    // --- Variant attribution: inject A/B variant into ALL tracked events ---
    // This enables funnel step 2 (conversion) to be filtered by variant:
    // "of users in variant A, how many converted?" vs "of users in variant B?"
    //
    // Variant values are cached on first call — cookies don't change during
    // a page load, so we scan document.cookie only once.
    function installVariantAttribution() {
        if (!hasConfigs()) return;
        var original = window.gaTrack;
        if (!original) return;

        var _variantCache = null;
        window.__gaAbInvalidateVariantCache = function () {
            _variantCache = null;
        };

        function getVariantMetadata() {
            if (_variantCache !== null) return _variantCache;
            _variantCache = {};
            if (!document.cookie) return _variantCache;

            var activeKeys = {};
            getConfigs().forEach(function (config) {
                activeKeys[String(config.funnel_id) + '_' + String(config.step_index)] = true;
            });

            var cookies = document.cookie.split('; ');
            for (var i = 0; i < cookies.length; i++) {
                var eq = cookies[i].indexOf('=');
                if (eq === -1) continue;
                var name = cookies[i].substring(0, eq);
                if (
                    name.indexOf(COOKIE_PREFIX) === 0 &&
                    name.indexOf(COOKIE_PREFIX + 'rc_') !== 0 &&
                    name.indexOf(COOKIE_PREFIX + 'v_') !== 0
                ) {
                    var key = name.substring(COOKIE_PREFIX.length);
                    var val = decodeURIComponent(cookies[i].substring(eq + 1));
                    if (activeKeys[key]) {
                        _variantCache['ab_' + key] = val;
                    }
                }
            }
            return _variantCache;
        }

        window.gaTrack = function (eventType, data) {
            data = data || {};
            data.metadata = data.metadata || {};

            // Merge cached variant data (scanned once, reused for all events)
            var variants = getVariantMetadata();
            for (var k in variants) {
                if (variants.hasOwnProperty(k)) {
                    data.metadata[k] = variants[k];
                }
            }

            return original.call(this, eventType, data);
        };
    }

    function initializeAbSplitter() {
        // Run synchronously for ordinary visits. A prerendered page must wait
        // for activation so it cannot assign cookies or redirect in private.
        // Configs embedded inline by PHP. Tracker loads before us (WP enqueue dep).
        if (!hasConfigs()) {
            return;
        }

        installVariantAttribution();
        installNavigationListeners();
        init();
    }

    if (document.prerendering) {
        document.addEventListener('prerenderingchange', initializeAbSplitter, {once: true});
    } else {
        initializeAbSplitter();
    }
})();
